import React, { useEffect, useState, useMemo } from "react";
import {
  Paper,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { FilterAlt } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "tss-react/mui";
import * as actionsNV02 from "../../store/actions/indexNV02";
import { AppState } from "../../store/rootReducer";
import CSharpUtils from "../../felles/CSharpUtils";
import { ProductGroupMetrics } from "../../models/NV02Lava";

// ------------------------
// Styles
// ------------------------
const useStyles = makeStyles()(() => ({
  root: {
    margin: 20,
    fontSize: "1em !important",
    color: "#333",
  },
  // This container is the MAIN scroll area (body is locked)
  container: {
    height: `calc(100vh - 220px)`, // adjust to match your header height
    overflowY: "auto",
    overflowX: "hidden",
  },
  tableHeader: {
    fontWeight: "bold",
  },
  gridItem: {
    backgroundColor: "rgba(29, 78, 137, 0.05)",
    padding: "20px 10px",
    borderRadius: "5px",
    textAlign: "center",
  },
  gridItemTitle: {
    fontWeight: "700",
    color: "#d30535",
    fontSize: "1.2rem",
  },
  gridItemValue: {
    fontSize: "1.2em",
    fontWeight: "bold",
    color: "#333",
  },
}));

// ------------------------
// Hook: lock body scroll (same idea as Vars)
// ------------------------
const useLockBodyScroll = () => {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow || "auto";
    };
  }, []);
};

// ------------------------
// Component
// ------------------------
const Lave = () => {
  const { classes } = useStyles();
  const dispatch = useDispatch();

  // Lock body scroll so only our internal container scrolls
  useLockBodyScroll();

  const productMetricsData = useSelector(
    (state: AppState) => state.NV02.ProductGroupMetrics
  );
  const warehouseMetricsData = useSelector(
    (state: AppState) => state.NV02.WarehouseMetrics
  );
  const productInfoData = useSelector(
    (state: AppState) => state.NV02.ProductInfo
  );
  const lagerKodeData = useSelector(
    (state: AppState) => state.NV02.LagerKode
  );

  const isFetchingGroup = useSelector(
    (s: AppState) => s.NV02.isFetchingGroup
  );
  const isFetchingProductInfo = useSelector(
    (s: AppState) => s.NV02.isFetchingProductInfo
  );
  const isFetchingLagerKode = useSelector(
    (s: AppState) => s.NV02.isFetchingLagerKode
  );

  const isMainLoading =
    isFetchingGroup || isFetchingProductInfo || isFetchingLagerKode;

  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [filters, setFilters] = useState<{ [key: string]: string }>({});
  const [filterColumn, setFilterColumn] = useState<string | null>(null);
  const [groupProducts, setGroupProducts] = useState(productInfoData ?? []);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selected, setSelected] = useState<number | null>(10); // Default to OSLO (lK10)

  const ProductGroupsdata = productMetricsData ?? [];
  const data = warehouseMetricsData ?? [];

  const columnKeyMap: Record<string, keyof ProductGroupMetrics> = {
    Varegruppe: "group",
    Beskrivelse: "description",
    "ANT. ARTIKLER": "lines",
    "ANT. MED BEH.": "linesWStock",
    Lagerverdi: "totalValue",
  };

  // ------------------------
  // Initial fetch
  // ------------------------
  useEffect(() => {
    dispatch(actionsNV02.fetchLagerKodeRequest());
  }, [dispatch]);

  useEffect(() => {
    const companyId = CSharpUtils.T_companyID;
    dispatch(actionsNV02.fetchProductMetricsRequest(companyId, selected));
    dispatch(actionsNV02.fetchWarehouseMetricsRequest(companyId, selected));
  }, [dispatch, selected]);

  // ------------------------
  // Lager options
  // ------------------------
  const options = useMemo<{ label: string; value: number | null }[]>(() => {
    if (!lagerKodeData || Array.isArray(lagerKodeData)) {
      return [];
    }

    const parsedOptions = Object.entries(lagerKodeData)
      .filter(
        ([key, val]) =>
          key.toLowerCase().startsWith("lk") && val && String(val).trim() !== ""
      )
      .map(([key, val]) => {
        const value = Number(key.replace(/lk/i, ""));
        return { label: String(val), value };
      });

    return [...parsedOptions, { label: "TOTALT LAGER", value: null }];
  }, [lagerKodeData]);

  const handleChange = (value: number | null) => {
    setSelected(value);
  };

  // ------------------------
  // Group click -> dialog open
  // ------------------------
  const handleGroupClick = (group: string) => {
    const companyId = CSharpUtils.T_companyID;
    dispatch(
      actionsNV02.fetchProductByGroupRequest(companyId, Number(group), selected)
    );
    setSelectedGroup(group);
    setIsModalOpen(true);
  };

  useEffect(() => {
    if (productInfoData) {
      setGroupProducts(productInfoData);
    }
  }, [productInfoData]);

  // ------------------------
  // Filters
  // ------------------------
  const handleFilterIconClick = (col: string) => {
    if (filterColumn === col) {
      setFilterColumn(null);
      setFilters((prev) => {
        const updated = { ...prev };
        delete updated[col];
        return updated;
      });
    } else {
      setFilterColumn(col);
    }
  };

  const handleFilterChange = (column: string, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [column]: value.toLowerCase(),
    }));
  };

  const applyFilters = (item: ProductGroupMetrics) => {
    return Object.keys(filters).every((key) => {
      const dataKey = columnKeyMap[key];
      const value = item[dataKey]?.toString().toLowerCase();
      return value ? value.includes(filters[key]) : false;
    });
  };

  const filteredData = useMemo(() => {
    return ProductGroupsdata.filter(applyFilters);
  }, [ProductGroupsdata, filters]);

  // ------------------------
  // Export: products in selected group (dialog)
  // ------------------------
  const handleExportClick = () => {
    if (!groupProducts || groupProducts.length === 0) return;

    const headers = [
      "Alfa",
      "Artikkelnummer",
      "Varebetegnelse",
      "Beholdning",
      "Selvkost",
      "Pristillegg",
      "Verdi",
    ];

    const csvContent = [
      headers.join(";"),
      ...groupProducts.map((item) =>
        [
          item.alpha || "",
          "'" + (item.productNumber || ""),
          item.description || "",
          item.stock ?? 0,
          Math.round(item.purchasePrice).toString() ?? 0,
          item.surcharge ?? 0,
          Math.round(item.value).toString(),
        ].join(";")
      ),
    ].join("\n");

    const utf8Bom = "\uFEFF";
    const blob = new Blob([utf8Bom + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `Lagerverdi_varegruppe${selectedGroup || "ukjent"}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ------------------------
  // Export: product groups (main table)
  // ------------------------
  const handleExportProductGroups = () => {
    if (!filteredData || filteredData.length === 0) return;

    const headers = [
      "VAREGRUPPE",
      "BESKRIVELSE",
      "ANT. ARTIKLER",
      "ANT.MED BEH",
      "LAGERVERDI",
    ];

    const csvContent = [
      headers.join(";"),
      ...filteredData.map((row) =>
        [
          row.group || "",
          row.description || "",
          row.lines ?? 0,
          row.linesWStock ?? 0,
          typeof row.totalValue === "number"
            ? Math.round(row.totalValue).toString()
            : 0,
        ].join(";")
      ),
    ].join("\n");

    const utf8Bom = "\uFEFF";
    const blob = new Blob([utf8Bom + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "Lagerverdi_Total.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ------------------------
  // Render
  // ------------------------
  return (
    <div className={classes.root}>
      {/* --- Top metrics --- */}
      <Paper
        elevation={3}
        sx={{ padding: 2, maxWidth: "1600px", margin: "20px auto" }}
      >
        <Grid container spacing={2} marginTop={1}>
          <Grid size={{ xs: 4 }} className={classes.gridItem}>
            <Typography className={classes.gridItemTitle}>
              TOTAL LAGERVERDI
            </Typography>
            <Typography className={classes.gridItemValue}>
              {data.length > 0
                ? (data[0].totalWarehouseValue ?? 0).toLocaleString("no-NO", {
                  style: "currency",
                  currency: "NOK",
                  maximumFractionDigits: 0,
                })
                : "N/A"}
            </Typography>
          </Grid>

          <Grid size={{ xs: 4 }} className={classes.gridItem}>
            <Typography className={classes.gridItemTitle}>
              ANT. ARTIKLER
            </Typography>
            <Typography className={classes.gridItemValue}>
              {data.length > 0
                ? (data[0].totalLines ?? 0).toLocaleString("no-NO")
                : "N/A"}
            </Typography>
          </Grid>

          <Grid size={{ xs: 4 }} className={classes.gridItem}>
            <Typography className={classes.gridItemTitle}>
              ANT. MED BEH.
            </Typography>
            <Typography className={classes.gridItemValue}>
              {data.length > 0
                ? (data[0].totalLinesWStock ?? 0).toLocaleString("no-NO")
                : "N/A"}
            </Typography>
          </Grid>
        </Grid>

        {/* --- Warehouse checkboxes & Excel --- */}
        <Box
          mt={3}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <FormGroup row>
            {options.map((opt) => (
              <FormControlLabel
                key={String(opt.value)}
                control={
                  <Checkbox
                    checked={selected === opt.value}
                    onChange={() => handleChange(opt.value as number | null)}
                    name={opt.label}
                  />
                }
                label={opt.label}
              />
            ))}
          </FormGroup>

          <Box display="flex" alignItems="center" gap={1}>
            <Button
              onClick={handleExportProductGroups}
              variant="contained"
              size="small"
              disabled={isFetchingGroup}
              sx={{
                backgroundColor: "#217346",
                color: "#fff",
                "&:hover": { backgroundColor: "#1e5f3f" },
              }}
            >
              {isFetchingGroup ? "Laster..." : "Excel"}
            </Button>

            <Tooltip
              title={
                <Typography sx={{ fontSize: "0.9rem", fontWeight: 500 }}>
                  Varelagerverdien oppdateres automatisk hver gang det skjer en
                  varetilgang eller et salg.
                  <br />
                  <br />
                  Øverst får du en samlet oversikt over firmaets totale
                  varelagerverdi. Du kan laste ned en oversikt over alle
                  varegrupper til Excel.
                  <br />
                  <br />
                  Nederst vises varelagerverdi per varegruppe. Klikk på en
                  varegruppe for detaljer og Excel-eksport.
                </Typography>
              }
              placement="bottom"
              arrow
            >
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  backgroundColor: "#2e7d32",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  fontSize: "0.9rem",
                  fontWeight: "bold",
                  userSelect: "none",
                }}
              >
                i
              </Box>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* --- Table or Loader --- */}
      {isMainLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="50vh"
        >
          <CircularProgress color="primary" size={50} />
        </Box>
      ) : (
        <TableContainer component={Paper} className={classes.container}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {[
                  "Varegruppe",
                  "Beskrivelse",
                  "ANT. ARTIKLER",
                  "ANT. MED BEH.",
                  "Lagerverdi",
                ].map((col) => {
                  const isNumeric = [
                    "ANT. ARTIKLER",
                    "ANT. MED BEH.",
                    "Lagerverdi",
                  ].includes(col);
                  return (
                    <TableCell
                      key={col}
                      className={classes.tableHeader}
                      align={isNumeric ? "right" : "left"}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent={
                          isNumeric ? "flex-end" : "space-between"
                        }
                      >
                        <Box display="flex" alignItems="center">
                          {col.toUpperCase()}
                          <IconButton
                            size="small"
                            onClick={() => handleFilterIconClick(col)}
                          >
                            <FilterAlt />
                          </IconButton>
                        </Box>
                      </Box>

                      {filterColumn === col && (
                        <TextField
                          size="small"
                          variant="outlined"
                          value={filters[col] || ""}
                          onChange={(e) =>
                            handleFilterChange(col, e.target.value)
                          }
                          sx={{ marginTop: 1 }}
                        />
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Ingen resultater funnet
                  </TableCell>
                </TableRow>
              ) : (
                filteredData.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography
                        sx={{
                          cursor: "pointer",
                          color: "blue",
                          textDecoration: "underline",
                        }}
                        onClick={() => handleGroupClick(row.group)}
                      >
                        {row.group}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.description || "N/A"}</TableCell>
                    <TableCell align="right">{row.lines}</TableCell>
                    <TableCell align="right">{row.linesWStock}</TableCell>
                    <TableCell align="right">
                      {typeof row.totalValue === "number"
                        ? row.totalValue.toLocaleString("no-NO", {
                          maximumFractionDigits: 0,
                        })
                        : "-"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* --- Modal Dialog --- */}
      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        maxWidth="xl"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          <Typography
            sx={{
              fontWeight: "700",
              fontSize: "1.2rem",
              color: "#d30535",
              textAlign: "center",
              textTransform: "uppercase",
            }}
          >
            Varer i valgt Varegruppe {selectedGroup}
          </Typography>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            maxHeight: "70vh",
            overflowY: "auto",
            p: 0,
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {[
                  "Alfa",
                  "Artikkelnummer",
                  "Varebetegnelse",
                  "Beholdning",
                  "Selvkost",
                  "Pristillegg",
                  "Verdi",
                ].map((header) => (
                  <TableCell
                    key={header}
                    sx={{
                      position: "sticky",
                      top: 0,
                      backgroundColor: (theme) =>
                        theme.palette.background.paper,
                      fontWeight: "bold",
                      zIndex: 2,
                      boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                    }}
                    align={
                      ["Beholdning", "Selvkost", "Pristillegg", "Verdi"].includes(
                        header
                      )
                        ? "right"
                        : "left"
                    }
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {isFetchingGroup ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : groupProducts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Ingen varer funnet
                  </TableCell>
                </TableRow>
              ) : (
                groupProducts.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.alpha}</TableCell>
                    <TableCell>{item.productNumber}</TableCell>
                    <TableCell>{item.description || "N/A"}</TableCell>
                    <TableCell align="right">
                      {item.stock?.toLocaleString("no-NO")}
                    </TableCell>
                    <TableCell align="right">
                      {item.purchasePrice?.toLocaleString("no-NO", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </TableCell>
                    <TableCell align="right">
                      {item.surcharge?.toLocaleString("no-NO", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </TableCell>
                    <TableCell align="right">
                      {item.value?.toLocaleString("no-NO", {
                        maximumFractionDigits: 0,
                      })}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleExportClick}
            color="success"
            variant="contained"
            disabled={isFetchingGroup}
          >
            Excel
          </Button>
          <Button onClick={() => setIsModalOpen(false)}>Lukk</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Lave;
